
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { CodeEditor } from './CodeEditor';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export function WebhookLogs() {
  const [selectedLog, setSelectedLog] = useState<any>(null);
  const [viewOpen, setViewOpen] = useState(false);
  
  // Query delivery logs with related data
  const { data: logs, isLoading } = useQuery({
    queryKey: ['webhook-logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('webhook_delivery_logs')
        .select(`
          *,
          webhook_queue (
            *,
            webhook_triggers (
              *,
              webhook_event_types (event_code),
              webhook_endpoints (url)
            )
          )
        `)
        .order('attempt_timestamp', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data;
    },
  });
  
  // View log details
  const handleViewDetails = (log: any) => {
    setSelectedLog(log);
    setViewOpen(true);
  };
  
  // Get status badge for response status
  const getStatusBadge = (status: number | null) => {
    if (status === null) {
      return <Badge variant="outline" className="bg-gray-300">No Response</Badge>;
    }
    
    if (status >= 200 && status < 300) {
      return <Badge className="bg-green-500">{status}</Badge>;
    } else if (status >= 400 && status < 500) {
      return <Badge className="bg-yellow-500">{status}</Badge>;
    } else if (status >= 500) {
      return <Badge className="bg-red-500">{status}</Badge>;
    } else {
      return <Badge>{status}</Badge>;
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Webhook Delivery Logs</CardTitle>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div>Loading logs...</div>
        ) : (
          <ScrollArea className="h-[60vh] w-full">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs?.map((log) => (
                  <TableRow key={log.log_id}>
                    <TableCell>
                      {format(new Date(log.attempt_timestamp), 'PPp')}
                    </TableCell>
                    <TableCell>
                      {log.webhook_queue?.webhook_triggers?.webhook_event_types?.event_code || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.response_status)}
                    </TableCell>
                    <TableCell>
                      {log.duration_ms ? `${log.duration_ms}ms` : 'N/A'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {log.webhook_queue?.webhook_triggers?.webhook_endpoints?.url || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewDetails(log)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
      
      {/* Details Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Webhook Delivery Log</DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Event</Label>
                  <p className="text-lg font-semibold">
                    {selectedLog.webhook_queue?.webhook_triggers?.webhook_event_types?.event_code || 'Unknown'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p className="text-lg">{getStatusBadge(selectedLog.response_status)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Timestamp</Label>
                  <p>{format(new Date(selectedLog.attempt_timestamp), 'PPpp')}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Duration</Label>
                  <p>{selectedLog.duration_ms ? `${selectedLog.duration_ms}ms` : 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Endpoint</Label>
                  <p>{selectedLog.webhook_queue?.webhook_triggers?.webhook_endpoints?.url || 'Unknown'}</p>
                </div>
              </div>
              
              {selectedLog.error_message && (
                <div>
                  <Label className="text-muted-foreground">Error</Label>
                  <p className="text-red-500">{selectedLog.error_message}</p>
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Request Headers</Label>
                <CodeEditor
                  value={JSON.stringify(selectedLog.request_headers, null, 2)}
                  language="json"
                  height="150px"
                  readOnly
                />
              </div>
              
              <div>
                <Label className="text-muted-foreground">Request Body</Label>
                <CodeEditor
                  value={JSON.stringify(selectedLog.request_body, null, 2)}
                  language="json"
                  height="200px"
                  readOnly
                />
              </div>
              
              {selectedLog.response_body && (
                <div>
                  <Label className="text-muted-foreground">Response Body</Label>
                  <CodeEditor
                    value={JSON.stringify(selectedLog.response_body, null, 2)}
                    language="json"
                    height="200px"
                    readOnly
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
